import { MouseEvent, useCallback, useEffect, useRef } from 'react';
import { useLocation } from 'react-router';

const positions = new Map<string, { x: number; y: number }>();

export const usePageScrollRestoration = (loaded: boolean, imageSelector?: string) => {
  const location = useLocation();
  const entryKey = `${location.key || 'initial'}:${location.pathname}${location.search}`;
  const pageRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const position = positions.get(entryKey);

    if (!loaded || !position || !pageRef.current) return;

    // Pages are fetched again on return. Gallery thumbnails with intrinsic
    // heights must settle before restoring a deep position.
    const images = imageSelector ? Array.from(pageRef.current.querySelectorAll<HTMLImageElement>(imageSelector)) : [];

    const pending = new Set(images.filter((image) => !image.complete));

    let frame: number;
    const restore = () => {
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(() => window.scrollTo(position.x, position.y));
    };

    const onSettled = (event: Event) => {
      pending.delete(event.currentTarget as HTMLImageElement);
      if (!pending.size) restore();
    };

    pending.forEach((image) => {
      image.addEventListener('load', onSettled);
      image.addEventListener('error', onSettled);
    });

    if (!pending.size) {
      restore();
    }

    return () => {
      cancelAnimationFrame(frame);
      images.forEach((image) => {
        image.removeEventListener('load', onSettled);
        image.removeEventListener('error', onSettled);
      });
    };
  }, [entryKey, imageSelector, loaded]);

  const saveScrollPosition = useCallback(
    (event: MouseEvent<HTMLElement>) => {
      const anchor = (event.target as Element).closest('a[href]');
      if (!anchor || !(anchor instanceof HTMLAnchorElement)) return;

      const destination = new URL(anchor.href);
      if (
        event.button !== 0 ||
        event.metaKey ||
        event.ctrlKey ||
        event.shiftKey ||
        event.altKey ||
        (anchor.target && anchor.target !== '_self') ||
        destination.origin !== window.location.origin
      ) {
        return;
      }

      positions.delete(entryKey);
      positions.set(entryKey, { x: window.scrollX, y: window.scrollY });

      // Keep history restoration bounded during long browsing sessions.
      if (positions.size > 50) {
        const oldestKey = positions.keys().next().value;
        if (oldestKey !== undefined) positions.delete(oldestKey);
      }
    },
    [entryKey],
  );

  return { pageRef, saveScrollPosition };
};
